import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { functions, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchQuery } from '../utils/textSanitize';
import { 
  Upload, 
  Search, 
  Filter, 
  Eye, 
  Download, 
  Check, 
  X, 
  FileText, 
  Calendar, 
  User, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Plus,
  Edit,
  Building2,
  Truck
} from 'lucide-react';

const DocumentsPage = () => {
  const { user, userData, hasPermission, getEmpresaId, isRole } = useAuth();
  /** empresa_interna = documentos só da empresa; fornecedores = docs vinculados a fornecedor; analise = pendentes */
  const [activeTab, setActiveTab] = useState('empresa_interna');
  const [documents, setDocuments] = useState([]);
  const [documentosParaAnalise, setDocumentosParaAnalise] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFiltroAnalise, setStatusFiltroAnalise] = useState('todos');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEmpresaUploadModal, setShowEmpresaUploadModal] = useState(false);
  const [supplierFilterFornecedores, setSupplierFilterFornecedores] = useState('');
  const [fornecedoresList, setFornecedoresList] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocumentoEnviado, setSelectedDocumentoEnviado] = useState(null);
  const [observacoesAprovacao, setObservacoesAprovacao] = useState('');
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit'
  const [stats, setStats] = useState({
    total: 0,
    pendentes: 0,
    aprovados: 0,
    rejeitados: 0,
    vencendo: 0
  });
  const [statsAnalise, setStatsAnalise] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [documentTypesOptions, setDocumentTypesOptions] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  const searchTimeoutRef = useRef(null);
  const tabInicializado = useRef(false);

  useLayoutEffect(() => {
    if (tabInicializado.current || !userData?.funcao) return;
    tabInicializado.current = true;
    if (isRole('admin') || isRole('qualidade')) {
      setActiveTab('empresa_interna');
    } else {
      setActiveTab('fornecedores');
    }
  }, [userData?.funcao, isRole]);

  // ✅ NOVO: Função para carregar tipos de documentos obrigatórios cadastrados pelo Superadmin
  const loadDocumentTypesOptions = async () => {
    try {
      setLoadingTypes(true);
      const getTypes = httpsCallable(functions, 'getDocumentTypesOptions');
      const result = await getTypes();
      
      if (result.data?.success) {
        setDocumentTypesOptions(result.data.data || []);
        console.log('✅ Tipos de documentos obrigatórios carregados:', result.data.data?.length);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tipos de documentos:', error);
      // Fallback vazio - não definir lista fixa
      setDocumentTypesOptions([]);
    } finally {
      setLoadingTypes(false);
    }
  };

  // ✅ NOVO: Carregar tipos de documentos obrigatórios ao montar o componente
  useEffect(() => {
    loadDocumentTypesOptions();
  }, []);

  useEffect(() => {
    if (!(isRole('admin') || isRole('qualidade'))) return;
    let cancelled = false;
    (async () => {
      try {
        const listSuppliers = httpsCallable(functions, 'listSuppliers');
        const empresaId = getEmpresaId();
        if (!empresaId) return;
        const result = await listSuppliers({
          id_empresa: empresaId,
          status: 'aprovado',
          page: 1,
          limit: 1000
        });
        if (!cancelled && result.data?.success) {
          setFornecedoresList(result.data.data.items || []);
        }
      } catch (e) {
        console.error('Erro ao carregar fornecedores (Documentos):', e);
      }
    })();
    return () => { cancelled = true; };
  }, [userData?.id_empresa]);

  useEffect(() => {
    if (activeTab === 'empresa_interna' || activeTab === 'fornecedores') {
      loadDocuments();
    } else if (activeTab === 'analise') {
      loadDocumentosParaAnalise();
    }
  }, [pagination.page, statusFilter, typeFilter, activeTab, statusFiltroAnalise, supplierFilterFornecedores]);

  useEffect(() => {
    // Debounce search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      if (searchTerm !== '') {
        loadDocuments();
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Função auxiliar para extrair mensagem de erro específica
  const getErrorMessage = (error) => {
    console.log('🔍 Analisando erro:', error);
    
    // Verificar se é um erro do Firebase Functions
    if (error.code && error.code.startsWith('functions/')) {
      return error.message || 'Erro na validação dos dados';
    }
    
    // Verificar códigos específicos
    if (error.code === 'functions/invalid-argument') {
      return error.message || 'Dados inválidos fornecidos';
    }
    
    if (error.code === 'functions/permission-denied') {
      return 'Você não tem permissão para realizar esta operação';
    }
    
    if (error.code === 'functions/not-found') {
      return 'Documento não encontrado';
    }
    
    if (error.code === 'functions/already-exists') {
      return 'Documento já existe no sistema';
    }
    
    // Verificar se há uma mensagem específica no erro
    if (error.message && error.message !== 'INTERNAL') {
      return error.message;
    }
    
    // Fallback para erro genérico
    return 'Erro ao conectar com o servidor';
  };

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 DocumentsPage: Carregando documentos...');

      const listDocuments = httpsCallable(functions, 'listDocuments');
      const empresaId = getEmpresaId();
      const listLimit =
        activeTab === 'fornecedores' && !supplierFilterFornecedores
          ? Math.min(100, pagination.limit)
          : pagination.limit;

      const listPayload = {
        page: pagination.page,
        limit: listLimit,
        search: searchTerm?.trim() ? sanitizeSearchQuery(searchTerm) : undefined,
        status: statusFilter || undefined,
        tipo: typeFilter || undefined,
        id_empresa: isRole('superadmin') ? undefined : empresaId
      };

      if (
        activeTab === 'empresa_interna' &&
        (isRole('admin') || isRole('qualidade') || isRole('superadmin'))
      ) {
        listPayload.somente_internos_empresa = true;
      } else if (activeTab === 'fornecedores' && supplierFilterFornecedores) {
        listPayload.id_fornecedor = supplierFilterFornecedores;
      }

      const result = await listDocuments(listPayload);

      console.log('🔍 DocumentsPage: Resultado da listDocuments:', result);

      if (result.data && result.data.success) {
        const data = result.data.data;
        
        // Converter timestamps do Firestore
        let convertedItems = (data.items || []).map(item => ({
          ...item,
          data_upload: item.data_upload?._seconds ? new Date(item.data_upload._seconds * 1000) : item.data_upload,
          data_expiracao: item.data_expiracao?._seconds ? new Date(item.data_expiracao._seconds * 1000) : item.data_expiracao,
          data_vencimento: item.data_expiracao?._seconds ? new Date(item.data_expiracao._seconds * 1000) : item.data_expiracao
        }));

        if (activeTab === 'fornecedores' && !supplierFilterFornecedores) {
          convertedItems = convertedItems.filter(
            (d) => d.id_fornecedor && d.documento_interno_empresa !== true
          );
        }

        setDocuments(convertedItems);

        // Calcular estatísticas (após filtros da aba Fornecedores)
        const statsData = {
          total: convertedItems.length,
          pendentes: convertedItems.filter(d => d.status === 'pendente').length,
          aprovados: convertedItems.filter(d => d.status === 'aprovado').length,
          rejeitados: convertedItems.filter(d => d.status === 'rejeitado').length,
          vencendo: convertedItems.filter(d => {
            if (!d.data_vencimento) return false;
            const vencimento = new Date(d.data_vencimento);
            const hoje = new Date();
            const diasRestantes = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
            return diasRestantes <= 30 && diasRestantes > 0;
          }).length
        };
        setStats(statsData);

        const totalPag =
          activeTab === 'fornecedores' && !supplierFilterFornecedores
            ? convertedItems.length
            : (data.pagination?.total || 0);
        const totalPagesPag =
          activeTab === 'fornecedores' && !supplierFilterFornecedores
            ? 1
            : (data.pagination?.totalPages || 0);

        setPagination(prev => ({
          ...prev,
          total: totalPag,
          totalPages: totalPagesPag
        }));
        
        console.log('✅ DocumentsPage: Documentos carregados:', data.items?.length || 0);
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar documentos');
      }

    } catch (error) {
      console.error('❌ DocumentsPage: Erro ao carregar documentos:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentosParaAnalise = async () => {
    try {
      setLoading(true);
      setError('');
      
      const empresaId = getEmpresaId();
      
      const listDocs = httpsCallable(functions, 'listDocumentosParaAnaliseQualidade');
      const result = await listDocs({
        empresa_id: empresaId,
        status_filtro: statusFiltroAnalise === 'todos' ? undefined : statusFiltroAnalise,
        page: pagination.page,
        limit: pagination.limit
      });

      if (result.data && result.data.success) {
        setDocumentosParaAnalise(result.data.data.documentos || []);
        setStatsAnalise(result.data.data.stats || {});
        setPagination(prev => ({
          ...prev,
          total: result.data.data.pagination.total,
          totalPages: result.data.data.pagination.totalPages
        }));
      }
    } catch (err) {
      console.error('❌ Erro ao carregar documentos para análise:', err);
      setError('Erro ao carregar documentos para análise.');
    } finally {
      setLoading(false);
    }
  };

  const handleAprovarDocumento = async (documentoId) => {
    if (!hasPermission('manage_documents')) {
      setError('Você não tem permissão para aprovar documentos.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const updateStatus = httpsCallable(functions, 'updateDocumentoEnviadoStatus');
      const result = await updateStatus({
        documento_id: documentoId,
        status: 'aprovado',
        aprovado_por: user?.uid
      });

      if (result.data.success) {
        setSuccess('Documento aprovado com sucesso!');
        setShowApprovalModal(false);
        loadDocumentosParaAnalise();
      } else {
        setError(result.data.message || 'Erro ao aprovar documento.');
      }
    } catch (err) {
      console.error('❌ Erro ao aprovar documento:', err);
      setError('Erro ao aprovar documento.');
    }
  };

  const handleRecusarDocumento = async (documentoId, observacoes) => {
    if (!hasPermission('manage_documents')) {
      setError('Você não tem permissão para recusar documentos.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      if (!observacoes || observacoes.trim() === '') {
        setError('Por favor, informe o motivo da recusa.');
        return;
      }

      const updateStatus = httpsCallable(functions, 'updateDocumentoEnviadoStatus');
      const result = await updateStatus({
        documento_id: documentoId,
        status: 'rejeitado',
        observacoes: observacoes,
        aprovado_por: user?.uid
      });

      if (result.data.success) {
        setSuccess('Documento recusado. O fornecedor poderá reenviar.');
        setShowApprovalModal(false);
        loadDocumentosParaAnalise();
      } else {
        setError(result.data.message || 'Erro ao recusar documento.');
      }
    } catch (err) {
      console.error('❌ Erro ao recusar documento:', err);
      setError('Erro ao recusar documento.');
    }
  };

  const handleOpenApprovalModal = (doc) => {
    setSelectedDocumentoEnviado(doc);
    setObservacoesAprovacao('');
    setShowApprovalModal(true);
    setError('');
    setSuccess('');
  };

  const handleUpload = async (files, documentType, supplierId) => {
    if (!hasPermission('manage_documents')) {
      setError('Você não tem permissão para fazer upload de documentos.');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');
    
    try {
      for (const file of Array.from(files)) {
        // Upload para Firebase Storage
        const storageRef = ref(storage, `documents/${getEmpresaId()}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        // Salvar metadados no Firestore
        const createDocument = httpsCallable(functions, 'createDocument');
        const result = await createDocument({
          nome: file.name,
          tipo: documentType,
          descricao: `Upload de ${documentType}`,
          url_arquivo: downloadURL,
          tamanho_arquivo: file.size,
          tipo_mime: file.type,
          id_empresa: getEmpresaId(),
          id_fornecedor: supplierId || null,
          documento_interno_empresa: false
        });
        
        console.log('✅ Documento criado:', result);
      }
      
      setSuccess('Documentos enviados com sucesso!');
      await loadDocuments();
      setShowUploadModal(false);
      
    } catch (error) {
      console.error('❌ Erro ao fazer upload:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleUploadEmpresa = async (nomeDocumento, file) => {
    if (!hasPermission('manage_documents')) {
      setError('Você não tem permissão para fazer upload de documentos.');
      return;
    }
    if (!nomeDocumento?.trim() || !file) {
      setError('Informe o nome do documento e selecione um arquivo.');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const storageRef = ref(
        storage,
        `documents/${getEmpresaId()}/empresa_interna/${Date.now()}_${file.name}`
      );
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      const createDocument = httpsCallable(functions, 'createDocument');
      await createDocument({
        nome: nomeDocumento.trim(),
        tipo: 'documento_empresa_propria',
        documento_interno_empresa: true,
        descricao: 'Documento interno da empresa',
        url_arquivo: downloadURL,
        tamanho_arquivo: file.size,
        tipo_mime: file.type,
        id_empresa: getEmpresaId()
      });

      setSuccess('Documento da empresa enviado com sucesso!');
      setShowEmpresaUploadModal(false);
      await loadDocuments();
    } catch (error) {
      console.error('Erro ao enviar documento da empresa:', error);
      setError(getErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const [loadingViewDocument, setLoadingViewDocument] = useState(false);
  const [openingDocumentId, setOpeningDocumentId] = useState(null);

  /** Abre o documento em nova aba (ao clicar no nome). Se não tiver URL na lista, busca com getDocument. */
  const handleOpenDocument = async (document) => {
    const url = document?.url_arquivo || document?.url;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!document?.id) {
      setError('Não foi possível abrir o documento: URL não disponível.');
      return;
    }
    try {
      setOpeningDocumentId(document.id);
      setError('');
      const getDocument = httpsCallable(functions, 'getDocument');
      const result = await getDocument({ document_id: document.id });
      const fullDoc = result.data?.success && result.data?.data?.document ? result.data.data.document : null;
      const docUrl = fullDoc?.url_arquivo || fullDoc?.url;
      if (docUrl) {
        window.open(docUrl, '_blank', 'noopener,noreferrer');
      } else {
        setError('Não foi possível abrir o documento: arquivo não encontrado.');
      }
    } catch (err) {
      console.error('Erro ao abrir documento:', err);
      setError(getErrorMessage(err));
    } finally {
      setOpeningDocumentId(null);
    }
  };

  const handleViewDocument = async (document) => {
    const hasUrl = document?.url_arquivo || document?.url;
    if (hasUrl) {
      setSelectedDocument(document);
      setModalMode('view');
      setShowDocumentModal(true);
      return;
    }
    // Se não tem URL na lista, buscar documento completo (pode ter url_arquivo no Firestore)
    if (!document?.id) {
      setSelectedDocument(document);
      setModalMode('view');
      setShowDocumentModal(true);
      return;
    }
    try {
      setLoadingViewDocument(true);
      const getDocument = httpsCallable(functions, 'getDocument');
      const result = await getDocument({ document_id: document.id });
      if (result.data?.success && result.data?.data?.document) {
        const fullDoc = result.data.data.document;
        const converted = {
          ...fullDoc,
          data_upload: fullDoc.data_upload?._seconds ? new Date(fullDoc.data_upload._seconds * 1000) : fullDoc.data_upload,
          data_expiracao: fullDoc.data_expiracao?._seconds ? new Date(fullDoc.data_expiracao._seconds * 1000) : fullDoc.data_expiracao,
          data_vencimento: (fullDoc.data_vencimento?._seconds ? new Date(fullDoc.data_vencimento._seconds * 1000) : fullDoc.data_vencimento) ||
            (fullDoc.data_expiracao?._seconds ? new Date(fullDoc.data_expiracao._seconds * 1000) : fullDoc.data_expiracao)
        };
        setSelectedDocument(converted);
      } else {
        setSelectedDocument(document);
      }
      setModalMode('view');
      setShowDocumentModal(true);
    } catch (err) {
      console.error('Erro ao buscar documento para visualização:', err);
      setSelectedDocument(document);
      setModalMode('view');
      setShowDocumentModal(true);
    } finally {
      setLoadingViewDocument(false);
    }
  };

  const handleEditDocument = (document) => {
    if (!hasPermission('manage_documents')) {
      setError('Você não tem permissão para editar documentos.');
      return;
    }
    setSelectedDocument(document);
    setModalMode('edit');
    setShowDocumentModal(true);
  };

  const handleSaveDocument = async (documentData) => {
    try {
      setError('');
      
      const updateDocument = httpsCallable(functions, 'updateDocument');
      const result = await updateDocument({
        document_id: selectedDocument.id,
        ...documentData
      });
      
      console.log('✅ Documento atualizado:', result);
      setSuccess('Documento atualizado com sucesso!');
      setShowDocumentModal(false);
      await loadDocuments();
      
    } catch (error) {
      console.error('❌ Erro ao atualizar documento:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      throw error; // Re-throw para o modal tratar
    }
  };

  const handleApproveDocument = async (documentId) => {
    if (!hasPermission('manage_documents')) {
      setError('Você não tem permissão para aprovar documentos.');
      return;
    }

    try {
      setError('');
      
      const approveDocument = httpsCallable(functions, 'approveDocument');
      const result = await approveDocument({
        document_id: documentId
      });
      
      console.log('✅ Documento aprovado:', result);
      setSuccess('Documento aprovado com sucesso!');
      await loadDocuments();
      
    } catch (error) {
      console.error('❌ Erro ao aprovar documento:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    }
  };

  const handleRejectDocument = async (documentId, motivo) => {
    if (!hasPermission('manage_documents')) {
      setError('Você não tem permissão para rejeitar documentos.');
      return;
    }

    try {
      setError('');
      
      const rejectDocument = httpsCallable(functions, 'rejectDocument');
      const result = await rejectDocument({
        document_id: documentId,
        motivo: motivo || 'Documento rejeitado'
      });
      
      console.log('✅ Documento rejeitado:', result);
      setSuccess('Documento rejeitado com sucesso!');
      await loadDocuments();
      
    } catch (error) {
      console.error('❌ Erro ao rejeitar documento:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'aprovado':
        return CheckCircle;
      case 'rejeitado':
        return X;
      case 'pendente':
        return Clock;
      default:
        return AlertTriangle;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      // Se já for um objeto Date
      if (date instanceof Date) {
        return !isNaN(date.getTime()) ? date.toLocaleDateString('pt-BR') : '-';
      }
      // Se for timestamp do Firestore com _seconds
      if (date._seconds) {
        return new Date(date._seconds * 1000).toLocaleDateString('pt-BR');
      }
      // Se for timestamp do Firestore com toDate()
      if (date.toDate) {
        return date.toDate().toLocaleDateString('pt-BR');
      }
      // Tentar converter string
      const d = new Date(date);
      return !isNaN(d.getTime()) ? d.toLocaleDateString('pt-BR') : '-';
    } catch (error) {
      return '-';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Verificar permissão
  if (!hasPermission('view_documents') && !hasPermission('manage_documents')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Acesso Negado</h3>
          <p className="text-gray-600">Você não tem permissão para visualizar documentos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Documentos</h1>
          <p className="text-gray-600">Gerencie documentos da sua empresa</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasPermission('manage_documents') &&
            activeTab === 'empresa_interna' &&
            (isRole('admin') || isRole('qualidade')) && (
              <button
                type="button"
                onClick={() => {
                  setShowEmpresaUploadModal(true);
                  setError('');
                  setSuccess('');
                }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Enviar documento da empresa
              </button>
            )}
          {hasPermission('manage_documents') &&
            activeTab === 'fornecedores' &&
            (isRole('admin') || isRole('qualidade')) && (
              <button
                type="button"
                onClick={() => {
                  setShowUploadModal(true);
                  setError('');
                  setSuccess('');
                }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                <Plus className="h-4 w-4" />
                Enviar documento de fornecedor
              </button>
            )}
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex items-center">
          <CheckCircle className="h-5 w-5 mr-2" />
          {success}
          <button 
            onClick={() => setSuccess('')}
            className="ml-auto text-green-700 hover:text-green-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <AlertTriangle className="h-5 w-5 mr-2" />
          {error}
          <button 
            onClick={() => setError('')}
            className="ml-auto text-red-700 hover:text-red-900"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      {(isRole('admin') || isRole('qualidade')) && (
        <div className="bg-white rounded-lg shadow">
          <div className="flex flex-wrap border-b border-gray-200">
            <button
              type="button"
              onClick={() => {
                setActiveTab('empresa_interna');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className={`px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'empresa_interna'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Empresa
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('fornecedores');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className={`px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'fornecedores'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4" />
                Fornecedores
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('analise');
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className={`px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'analise'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Pendentes de Aprovação
                {statsAnalise?.em_analise > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                    {statsAnalise.em_analise}
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {(activeTab === 'empresa_interna' || activeTab === 'fornecedores') && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pendentes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.pendentes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Aprovados</p>
                <p className="text-2xl font-bold text-gray-900">{stats.aprovados}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <X className="h-8 w-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Rejeitados</p>
                <p className="text-2xl font-bold text-gray-900">{stats.rejeitados}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Vencendo</p>
                <p className="text-2xl font-bold text-gray-900">{stats.vencendo}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analise' && statsAnalise && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="bg-white p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-gray-900">{statsAnalise.total}</p>
            <p className="text-sm text-gray-600">Total</p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-blue-600">{statsAnalise.em_analise}</p>
            <p className="text-sm text-blue-600">Em Análise</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-green-600">{statsAnalise.aprovados}</p>
            <p className="text-sm text-green-600">Aprovados</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-red-600">{statsAnalise.rejeitados}</p>
            <p className="text-sm text-red-600">Recusados</p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-orange-600">{statsAnalise.vencidos}</p>
            <p className="text-sm text-orange-600">Vencidos</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg shadow text-center">
            <p className="text-2xl font-bold text-yellow-600">{statsAnalise.alertas_criticos}</p>
            <p className="text-sm text-yellow-600">Alertas</p>
          </div>
        </div>
      )}

      {/* Filtros — abas Empresa / Fornecedores */}
      {(activeTab === 'empresa_interna' || activeTab === 'fornecedores') && (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className={`grid grid-cols-1 gap-4 ${activeTab === 'fornecedores' ? 'md:grid-cols-2 lg:grid-cols-6' : 'md:grid-cols-2 lg:grid-cols-4'}`}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar documentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Todos os Status</option>
            <option value="pendente">Pendente</option>
            <option value="aprovado">Aprovado</option>
            <option value="rejeitado">Rejeitado</option>
            <option value="vencido">Vencido</option>
          </select>

          {activeTab === 'fornecedores' && (isRole('admin') || isRole('qualidade')) && (
            <select
              value={supplierFilterFornecedores}
              onChange={(e) => {
                setSupplierFilterFornecedores(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos os fornecedores</option>
              {fornecedoresList.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nome_empresa || f.nome || f.razao_social || f.id}
                </option>
              ))}
            </select>
          )}

          {activeTab === 'fornecedores' && (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loadingTypes}
            >
              <option value="">Todos os Tipos</option>
              {loadingTypes ? (
                <option value="" disabled>Carregando tipos...</option>
              ) : (
                documentTypesOptions.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))
              )}
            </select>
          )}

          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setTypeFilter('');
              setSupplierFilterFornecedores('');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
          >
            <Filter className="h-4 w-4" />
            <span>Limpar Filtros</span>
          </button>
        </div>
        {activeTab === 'fornecedores' && !supplierFilterFornecedores && (
          <p className="text-xs text-gray-500 mt-3">
            Sem fornecedor selecionado, são exibidos até 100 documentos vinculados a fornecedores (filtro aplicado nesta página).
          </p>
        )}
      </div>
      )}

      {/* Filtros - Aba de Análise */}
      {activeTab === 'analise' && (
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex gap-4">
            <select
              value={statusFiltroAnalise}
              onChange={(e) => {
                setStatusFiltroAnalise(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="todos">Todos os Status</option>
              <option value="em_analise">Em Análise</option>
              <option value="aprovado">Aprovados</option>
              <option value="rejeitado">Recusados</option>
            </select>
          </div>
        </div>
      )}

      {/* Conteúdo — Empresa / Fornecedores */}
      {(activeTab === 'empresa_interna' || activeTab === 'fornecedores') && (
        <>
      {/* Documents Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum documento encontrado</h3>
              <p className="text-gray-600">Não há documentos cadastrados ou que correspondam aos filtros aplicados.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Documento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    {activeTab === 'fornecedores' &&
                      (isRole('superadmin') || isRole('qualidade') || isRole('admin')) && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fornecedor
                      </th>
                    )}
                    {isRole('superadmin') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Empresa
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data Upload
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vencimento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((document) => {
                    const StatusIcon = getStatusIcon(document.status);
                    return (
                      <tr key={document.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="h-8 w-8 text-gray-400 mr-3 flex-shrink-0" />
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => handleOpenDocument(document)}
                                disabled={openingDocumentId === document.id}
                                className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                                title="Clique para abrir o documento"
                              >
                                {openingDocumentId === document.id ? 'Abrindo...' : document.nome}
                              </button>
                              <div className="text-sm text-gray-500">
                                {formatFileSize(document.tamanho_arquivo || document.tamanho || 0)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {activeTab === 'empresa_interna'
                              ? 'Documento da empresa'
                              : document.tipo}
                          </span>
                        </td>
                        {activeTab === 'fornecedores' &&
                          (isRole('superadmin') || isRole('qualidade') || isRole('admin')) && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {document.fornecedor_nome || 'N/A'}
                          </td>
                        )}
                        {isRole('superadmin') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {document.empresa_nome || 'N/A'}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            document.status === 'aprovado' ? 'bg-green-100 text-green-800' :
                            document.status === 'rejeitado' ? 'bg-red-100 text-red-800' :
                            document.status === 'pendente' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {document.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(document.data_upload)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(document.data_vencimento)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewDocument(document)}
                              disabled={loadingViewDocument}
                              className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-wait"
                              title={loadingViewDocument ? 'Carregando...' : 'Visualizar'}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {hasPermission('manage_documents') && (
                              <>
                                <button
                                  onClick={() => handleEditDocument(document)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                {document.status === 'pendente' && (
                                  <>
                                    <button
                                      onClick={() => handleApproveDocument(document.id)}
                                      className="text-green-600 hover:text-green-900"
                                      title="Aprovar"
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => handleRejectDocument(document.id)}
                                      className="text-red-600 hover:text-red-900"
                                      title="Rejeitar"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                            {(document.url || document.url_arquivo) && (
                              <a
                                href={document.url || document.url_arquivo}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-purple-600 hover:text-purple-900"
                                title="Download"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando página <span className="font-medium">{pagination.page}</span> de{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                        disabled={pagination.page === pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

        </>
      )}

      {/* Conteúdo da Aba de Análise */}
      {activeTab === 'analise' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : documentosParaAnalise.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum documento para análise</h3>
                <p className="text-gray-600">Não há documentos pendentes de aprovação no momento.</p>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {documentosParaAnalise.map(doc => (
                <div key={doc.id} className={`border rounded-lg p-4 ${
                  doc.alerta?.urgencia === 'critica' ? 'border-red-300 bg-red-50' :
                  doc.alerta?.urgencia === 'alta' ? 'border-orange-300 bg-orange-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="h-5 w-5 text-gray-600" />
                        <h3 className="text-lg font-semibold text-gray-900">{doc.documento_nome}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          doc.status === 'em_analise' ? 'bg-blue-100 text-blue-800' :
                          doc.status === 'aprovado' ? 'bg-green-100 text-green-800' :
                          doc.status === 'rejeitado' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {doc.status === 'em_analise' && '🔄 Em Análise'}
                          {doc.status === 'aprovado' && '✅ Aprovado'}
                          {doc.status === 'rejeitado' && '❌ Recusado'}
                          {doc.status === 'pendente' && '📋 Pendente'}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">Fornecedor:</span> {doc.fornecedor_nome}
                        </div>
                        <div>
                          <span className="font-medium">CNPJ:</span> {doc.fornecedor_cnpj}
                        </div>
                        <div>
                          <span className="font-medium">Tipo:</span> {doc.documento_tipo}
                        </div>
                        <div>
                          <span className="font-medium">Enviado em:</span>{' '}
                          {doc.data_upload?.toDate ? doc.data_upload.toDate().toLocaleString('pt-BR') : 'N/A'}
                        </div>
                      </div>

                      {doc.alerta && (
                        <div className="flex items-center gap-2 text-sm font-medium mb-3">
                          <AlertTriangle className={`h-4 w-4 ${
                            doc.alerta.urgencia === 'critica' ? 'text-red-600' :
                            doc.alerta.urgencia === 'alta' ? 'text-orange-600' :
                            'text-yellow-600'
                          }`} />
                          <span className={
                            doc.alerta.urgencia === 'critica' ? 'text-red-600' :
                            doc.alerta.urgencia === 'alta' ? 'text-orange-600' :
                            'text-yellow-600'
                          }>
                            {doc.alerta.tipo === 'vencido' && `Vencido há ${Math.abs(doc.dias_para_vencimento)} dias`}
                            {doc.alerta.tipo === 'critico' && `Vence em ${doc.dias_para_vencimento} dias - URGENTE!`}
                            {doc.alerta.tipo === 'atencao' && `Vence em ${doc.dias_para_vencimento} dias`}
                          </span>
                        </div>
                      )}

                      <div className="bg-gray-50 p-3 rounded flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-700">{doc.nome_arquivo}</span>
                          <span className="text-xs text-gray-500">
                            ({(doc.tamanho_arquivo / 1024).toFixed(0)} KB)
                          </span>
                        </div>
                        <a
                          href={doc.url_arquivo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          Visualizar
                        </a>
                      </div>

                      {doc.observacoes && (
                        <div className="mt-3 text-sm text-red-600">
                          <span className="font-medium">Observações:</span> {doc.observacoes}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 mt-4">
                    {doc.status === 'em_analise' && hasPermission('manage_documents') && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedDocumentoEnviado(doc);
                            handleAprovarDocumento(doc.id);
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <CheckCircle className="h-4 w-4" />
                          Aprovar
                        </button>
                        <button
                          onClick={() => handleOpenApprovalModal(doc)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <XCircle className="h-4 w-4" />
                          Recusar
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload — documento interno da empresa */}
      {showEmpresaUploadModal && (
        <EmpresaUploadModal
          isOpen={showEmpresaUploadModal}
          onClose={() => setShowEmpresaUploadModal(false)}
          onSubmit={handleUploadEmpresa}
          uploading={uploading}
        />
      )}

      {/* Upload — documento de fornecedor */}
      {showUploadModal && (
        <UploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
          uploading={uploading}
          suppliers={fornecedoresList}
          requireSupplier
        />
      )}

      {/* Document Modal */}
      {showDocumentModal && (
        <DocumentModal
          isOpen={showDocumentModal}
          onClose={() => setShowDocumentModal(false)}
          onSave={handleSaveDocument}
          document={selectedDocument}
          mode={modalMode}
        />
      )}

      {/* Modal de Aprovação/Recusa */}
      {showApprovalModal && selectedDocumentoEnviado && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Recusar Documento</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedDocumentoEnviado.documento_nome}</p>
              </div>
              <button onClick={() => setShowApprovalModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Motivo da Recusa *
              </label>
              <textarea
                value={observacoesAprovacao}
                onChange={(e) => setObservacoesAprovacao(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Descreva o motivo da recusa..."
                required
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowApprovalModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRecusarDocumento(selectedDocumentoEnviado.id, observacoesAprovacao)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirmar Recusa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmpresaUploadModal = ({ isOpen, onClose, onSubmit, uploading }) => {
  const [nome, setNome] = useState('');
  const [file, setFile] = useState(null);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setNome('');
      setFile(null);
      setLocalError('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nome.trim()) {
      setLocalError('Informe o nome do documento.');
      return;
    }
    if (!file) {
      setLocalError('Selecione o arquivo.');
      return;
    }
    setLocalError('');
    onSubmit(nome.trim(), file);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Documento da empresa</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" disabled={uploading}>
            <X className="h-6 w-6" />
          </button>
        </div>
        {localError && (
          <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{localError}</div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome do documento *</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex.: Política de qualidade interna"
              disabled={uploading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arquivo *</label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              disabled={uploading}
            />
            <p className="text-xs text-gray-500 mt-1">Formatos aceitos: PDF, DOC, DOCX, JPG, PNG</p>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={uploading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUpload, uploading, suppliers = [], requireSupplier = false }) => {
  const [files, setFiles] = useState([]);
  const [documentType, setDocumentType] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [formError, setFormError] = useState('');

  // Carregar tipos de documentos quando o modal abre
  useEffect(() => {
    if (isOpen) {
      loadDocumentTypes();
      setFormError('');
      setSupplierId('');
    }
  }, [isOpen]);

  const loadDocumentTypes = async () => {
    try {
      setLoadingTypes(true);
      const getTypes = httpsCallable(functions, 'getDocumentTypesOptions');
      const result = await getTypes();
      
      console.log('🔍 getDocumentTypesOptions resultado:', result);
      console.log('🔍 Tipos retornados:', result.data?.data);
      
      if (result.data?.success) {
        setDocumentTypes(result.data.data || []);
        console.log('✅ Tipos de documentos carregados:', result.data.data?.length);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tipos de documentos:', error);
      // Fallback para tipos padrão
      setDocumentTypes([
        { value: 'certificado', label: 'Certificado' },
        { value: 'licenca', label: 'Licença' },
        { value: 'alvara', label: 'Alvará' },
        { value: 'outros', label: 'Outros' }
      ]);
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (requireSupplier && !supplierId) {
      setFormError('Selecione o fornecedor.');
      return;
    }
    if (files.length > 0 && documentType) {
      onUpload(files, documentType, supplierId);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Upload de Documento</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{formError}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Documento *
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={loadingTypes}
            >
              <option value="">
                {loadingTypes ? 'Carregando tipos...' : 'Selecione o tipo'}
              </option>
              {documentTypes.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </option>
              ))}
            </select>
            {loadingTypes && (
              <p className="text-xs text-gray-500 mt-1">
                Carregando tipos de documentos cadastrados...
              </p>
            )}
          </div>

          {requireSupplier && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor *</label>
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={uploading}
              >
                <option value="">Selecione o fornecedor</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome_empresa || s.nome || s.razao_social || s.id}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Arquivos *
            </label>
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              Formatos aceitos: PDF, DOC, DOCX, JPG, PNG
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={uploading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={uploading || files.length === 0 || !documentType}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {uploading ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== MODAL COMPLETO INTEGRADO =====
const DocumentModal = ({ isOpen, onClose, onSave, document, mode }) => {
  const [formData, setFormData] = useState({
    nome: '',
    tipo: '',
    descricao: '',
    data_vencimento: '',
    fornecedor_id: '',
    observacoes: '',
    status: 'pendente',
    tags: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [documentTypesOptions, setDocumentTypesOptions] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  // Função auxiliar para extrair mensagem de erro específica
  const getErrorMessage = (error) => {
    console.log('🔍 Analisando erro no DocumentModal:', error);
    
    // Verificar se é um erro do Firebase Functions
    if (error.code && error.code.startsWith('functions/')) {
      return error.message || 'Erro na validação dos dados';
    }
    
    // Verificar códigos específicos
    if (error.code === 'functions/invalid-argument') {
      return error.message || 'Dados inválidos fornecidos';
    }
    
    if (error.code === 'functions/permission-denied') {
      return 'Você não tem permissão para realizar esta operação';
    }
    
    if (error.code === 'functions/not-found') {
      return 'Documento não encontrado';
    }
    
    if (error.code === 'functions/already-exists') {
      return 'Documento com este nome já existe';
    }
    
    // Verificar se há uma mensagem específica no erro
    if (error.message && error.message !== 'INTERNAL') {
      return error.message;
    }
    
    // Fallback para erro genérico
    return 'Erro ao salvar documento';
  };

  // ✅ NOVO: Função para carregar tipos de documentos obrigatórios
  const loadDocumentTypesOptions = async () => {
    try {
      setLoadingTypes(true);
      const getTypes = httpsCallable(functions, 'getDocumentTypesOptions');
      const result = await getTypes();
      
      if (result.data?.success) {
        setDocumentTypesOptions(result.data.data || []);
        console.log('✅ Tipos de documentos carregados no modal:', result.data.data?.length);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar tipos de documentos no modal:', error);
      setDocumentTypesOptions([]);
    } finally {
      setLoadingTypes(false);
    }
  };

  // ✅ NOVO: Carregar tipos de documentos quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      loadDocumentTypesOptions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (document && (mode === 'edit' || mode === 'view')) {
      setFormData({
        nome: document.nome || '',
        tipo: document.tipo || '',
        descricao: document.descricao || '',
        data_vencimento: document.data_vencimento ? 
          (document.data_vencimento.toDate ? 
            document.data_vencimento.toDate().toISOString().split('T')[0] : 
            document.data_vencimento.split('T')[0]) : '',
        fornecedor_id: document.fornecedor_id || '',
        observacoes: document.observacoes || '',
        status: document.status || 'pendente',
        tags: Array.isArray(document.tags) ? document.tags.join(', ') : (document.tags || '')
      });
    } else {
      setFormData({
        nome: '',
        tipo: '',
        descricao: '',
        data_vencimento: '',
        fornecedor_id: '',
        observacoes: '',
        status: 'pendente',
        tags: ''
      });
    }
    setError('');
    setValidationErrors({});
  }, [document, mode, isOpen]);

  const validateForm = () => {
    const errors = {};

    // Validações obrigatórias
    if (!formData.nome.trim()) {
      errors.nome = 'Nome do documento é obrigatório';
    } else if (formData.nome.length < 3) {
      errors.nome = 'Nome deve ter pelo menos 3 caracteres';
    } else if (formData.nome.length > 200) {
      errors.nome = 'Nome deve ter no máximo 200 caracteres';
    }

    if (!formData.tipo) {
      errors.tipo = 'Tipo de documento é obrigatório';
    }

    // Validação de data de vencimento
    if (formData.data_vencimento) {
      const vencimento = new Date(formData.data_vencimento);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      
      if (vencimento < hoje) {
        errors.data_vencimento = 'Data de vencimento não pode ser no passado';
      }
    }

    // Validação de descrição
    if (formData.descricao && formData.descricao.length > 1000) {
      errors.descricao = 'Descrição deve ter no máximo 1000 caracteres';
    }

    // Validação de observações
    if (formData.observacoes && formData.observacoes.length > 2000) {
      errors.observacoes = 'Observações devem ter no máximo 2000 caracteres';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (mode === 'view') {
      onClose();
      return;
    }

    // Validar formulário
    if (!validateForm()) {
      setError('Por favor, corrija os erros no formulário');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Preparar dados para envio
      const dataToSend = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        data_vencimento: formData.data_vencimento || null
      };

      // Remover campos vazios
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '' || dataToSend[key] === null) {
          delete dataToSend[key];
        }
      });

      await onSave(dataToSend);
      onClose();
    } catch (error) {
      console.error('Erro no modal:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Limpar erro de validação específico quando o usuário começar a digitar
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: '' });
    }
  };

  const handleDownload = () => {
    const url = document?.url || document?.url_arquivo;
    if (url) {
      window.open(url, '_blank');
    } else {
      console.error('URL do documento não encontrada:', document);
      alert('URL do documento não encontrada');
    }
  };

  const handleView = () => {
    const url = document?.url || document?.url_arquivo;
    if (url) {
      window.open(url, '_blank');
    } else {
      console.error('URL do documento não encontrada:', document);
      alert('URL do documento não encontrada');
    }
  };

  if (!isOpen) return null;

  const statusOptions = [
    { value: 'pendente', label: 'Pendente', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'aprovado', label: 'Aprovado', color: 'bg-green-100 text-green-800' },
    { value: 'rejeitado', label: 'Rejeitado', color: 'bg-red-100 text-red-800' },
    { value: 'vencido', label: 'Vencido', color: 'bg-gray-100 text-gray-800' }
  ];

  const formatDate = (dateVal) => {
    if (!dateVal) return '-';
    let date;
    if (dateVal && typeof dateVal.toDate === 'function') date = dateVal.toDate();
    else if (dateVal._seconds != null) date = new Date(dateVal._seconds * 1000);
    else date = new Date(dateVal);
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {mode === 'edit' ? 'Editar Documento' : 'Visualizar Documento'}
          </h2>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        {/* Informações do arquivo (apenas no modo view/edit) */}
        {document && (
          <div className="bg-blue-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Informações do Arquivo
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Arquivo:</span>
                <span className="ml-2 text-blue-700">{document.nome_arquivo || document.nome || 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Tamanho:</span>
                <span className="ml-2 text-blue-700">{(document.tamanho_arquivo ?? document.tamanho) ? `${((document.tamanho_arquivo ?? document.tamanho) / 1024).toFixed(2)} KB` : 'N/A'}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Enviado em:</span>
                <span className="ml-2 text-blue-700">
                  {document.data_upload ? formatDate(document.data_upload) : 'N/A'}
                </span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Enviado por:</span>
                <span className="ml-2 text-blue-700">{document.enviado_por || 'N/A'}</span>
              </div>
            </div>
            {(document.url || document.url_arquivo) && (
              <div className="mt-4 flex space-x-3">
                <button
                  onClick={handleView}
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Abrir em Nova Aba
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </button>
              </div>
            )}
            {!document.url && !document.url_arquivo && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ URL do documento não disponível. O arquivo pode ter sido removido ou não foi carregado corretamente.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ✅ NOVO: Visualizador de Documento Embutido */}
        {document && (document.url || document.url_arquivo) && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Eye className="h-5 w-5 mr-2" />
              Visualização do Documento
            </h3>
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white" style={{ minHeight: '500px', maxHeight: '70vh' }}>
              {(() => {
                const docUrl = document.url || document.url_arquivo;
                const fileName = document.nome_arquivo || document.nome || '';
                const isPDF = fileName.toLowerCase().endsWith('.pdf') || docUrl.includes('.pdf');
                const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName) || 
                               /image\//.test(document.tipo_mime || '');
                
                if (isPDF) {
                  return (
                    <iframe
                      src={docUrl}
                      className="w-full h-full"
                      style={{ minHeight: '500px', maxHeight: '70vh' }}
                      title="Visualizador de PDF"
                    />
                  );
                } else if (isImage) {
                  return (
                    <div className="flex items-center justify-center p-4">
                      <img
                        src={docUrl}
                        alt={fileName}
                        className="max-w-full max-h-[70vh] object-contain rounded-lg"
                        onError={(e) => {
                          console.error('Erro ao carregar imagem:', docUrl);
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="p-4 text-center text-gray-600">
                              <p>Erro ao carregar imagem. Use o botão "Abrir em Nova Aba" ou "Download".</p>
                            </div>
                          `;
                        }}
                      />
                    </div>
                  );
                } else {
                  return (
                    <div className="p-8 text-center">
                      <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">
                        Visualização não disponível para este tipo de arquivo.
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        Tipo: {document.tipo_mime || 'Desconhecido'}
                      </p>
                      <div className="flex justify-center space-x-3">
                        <button
                          onClick={handleView}
                          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Abrir em Nova Aba
                        </button>
                        <button
                          onClick={handleDownload}
                          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </button>
                      </div>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Básicas</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Documento *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.nome ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                  disabled={mode === 'view'}
                  placeholder="Ex: Certificado Sanitário - Empresa ABC"
                />
                {validationErrors.nome && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.nome}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Documento *
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => handleInputChange('tipo', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.tipo ? 'border-red-500' : 'border-gray-300'
                  }`}
                  required
                  disabled={mode === 'view' || loadingTypes}
                >
                  <option value="">
                    {loadingTypes ? 'Carregando tipos...' : 'Selecione o tipo'}
                  </option>
                  {documentTypesOptions.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
                {loadingTypes && (
                  <p className="text-xs text-gray-500 mt-1">
                    Carregando tipos de documentos cadastrados...
                  </p>
                )}
                {validationErrors.tipo && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.tipo}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Vencimento
                </label>
                <input
                  type="date"
                  value={formData.data_vencimento}
                  onChange={(e) => handleInputChange('data_vencimento', e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.data_vencimento ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={mode === 'view'}
                />
                {validationErrors.data_vencimento && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.data_vencimento}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={mode === 'view'}
                >
                  {statusOptions.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
                {mode === 'view' && (
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(formData.status)}`}>
                      {statusOptions.find(opt => opt.value === formData.status)?.label || formData.status}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <textarea
                value={formData.descricao}
                onChange={(e) => handleInputChange('descricao', e.target.value)}
                rows={3}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.descricao ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={mode === 'view'}
                placeholder="Descrição detalhada do documento..."
              />
              {validationErrors.descricao && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.descricao}</p>
              )}
            </div>
          </div>

          {/* Informações Adicionais */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Adicionais</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID do Fornecedor
                </label>
                <input
                  type="text"
                  value={formData.fornecedor_id}
                  onChange={(e) => handleInputChange('fornecedor_id', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={mode === 'view'}
                  placeholder="ID do fornecedor relacionado"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags (separadas por vírgula)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={mode === 'view'}
                  placeholder="Ex: urgente, renovação, qualidade"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows={4}
                className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.observacoes ? 'border-red-500' : 'border-gray-300'
                }`}
                disabled={mode === 'view'}
                placeholder="Observações adicionais sobre o documento..."
              />
              {validationErrors.observacoes && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.observacoes}</p>
              )}
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {mode === 'view' ? 'Fechar' : 'Cancelar'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Salvando...
                  </span>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentsPage;

